import React, { useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState } from "../../store";
import { createItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert } from "../utils/messages";
import {
  ReceivedDocument,
  CREATION_TYPE_CDC,
  DOCUMENT_TYPE_LABELS,
  SALE_TYPE_CASH_LABEL,
  SALE_TYPE_CREDIT_LABEL,
} from "../../models/received-document";
import { getRoutePath } from "../../routes";
import { PYG } from "../../currency/available-currencies";

interface ReceivedDocumentCdcPreviewProps {
  data: ReceivedDocument;
  cdc: string;
  onCancel: () => void;
}

const formatAmount = (amount: number | undefined, currency: string): string => {
  if (amount === undefined || amount === null) return "—";
  if (currency === PYG) {
    return `₲ ${Math.round(amount).toLocaleString("es-PY")}`;
  }
  return `${currency} ${amount.toLocaleString("es-PY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PreviewRow: React.FC<{ label: string; value: string | undefined }> = ({
  label,
  value,
}) => (
  <CRow className="mb-2">
    <CCol md={4}>
      <strong>{label}:</strong>
    </CCol>
    <CCol md={8}>{value ?? "—"}</CCol>
  </CRow>
);

const ReceivedDocumentCdcPreview: React.FC<ReceivedDocumentCdcPreviewProps> = ({
  data,
  cdc,
  onCancel,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();

  const currency = data.currency ?? PYG;

  const onConfirm = async () => {
    setSubmitting(true);

    const payload: ReceivedDocument = {
      ...data,
      cdc: cdc,
      taxPayerId: taxPayer.id,
      clientRuc: data.clientRuc ?? taxPayer.ruc,
      clientSocialName: data.clientSocialName ?? taxPayer.socialName,
      creationType: CREATION_TYPE_CDC,
    };

    const status = await createItem<ReceivedDocument>(
      "/received_documents/create/",
      payload,
    );

    setSubmitting(false);

    if (status.status !== SUCCESS) {
      let message = "Ha ocurrido un error al guardar el documento";
      if (status.detail !== undefined) {
        message = status.detail;
      }
      errorAlert(message);
      return;
    }

    successAlert("Documento agregado exitosamente");
    history.push(getRoutePath("receivedDocuments"));
  };

  const saleTypeLabel =
    data.saleType === "cash"
      ? SALE_TYPE_CASH_LABEL
      : data.saleType === "credit"
        ? SALE_TYPE_CREDIT_LABEL
        : undefined;

  return (
    <CCard className="shadow-sm border-0">
      <CCardHeader
        className="d-flex align-items-center"
        style={{
          background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
          color: "white",
          borderBottom: "none",
          padding: "14px 20px",
        }}
      >
        <i className="fa fa-check-circle mr-2" style={{ fontSize: "1.1rem" }} />
        <span style={{ fontWeight: 600, fontSize: "1rem" }}>
          Documento encontrado en SIFEN
        </span>
      </CCardHeader>
      <CCardBody>
        <CRow className="mb-3">
          <CCol>
            <div
              className="alert alert-success"
              style={{ fontSize: "0.9rem" }}
            >
              Los datos fueron obtenidos automáticamente desde SIFEN. Revisá la
              información y confirmá para guardar.
            </div>
          </CCol>
        </CRow>

        <h6 className="text-muted mb-3">Datos del comprobante</h6>

        <PreviewRow
          label="Tipo"
          value={
            data.documentType ? DOCUMENT_TYPE_LABELS[data.documentType] : undefined
          }
        />
        <PreviewRow label="N° Documento" value={data.documentNumber} />
        <PreviewRow
          label="Fecha"
          value={
            data.documentDate
              ? new Date(data.documentDate + "T00:00:00").toLocaleDateString(
                  "es-PY",
                )
              : undefined
          }
        />
        <PreviewRow label="Condición de venta" value={saleTypeLabel} />
        <PreviewRow label="Moneda" value={data.currency} />
        {currency !== PYG && (
          <PreviewRow
            label="Tipo de cambio"
            value={
              data.pygExchangeRate !== undefined
                ? String(data.pygExchangeRate)
                : undefined
            }
          />
        )}

        <hr />
        <h6 className="text-muted mb-3">Emisor</h6>

        <PreviewRow label="RUC" value={data.supplierRuc} />
        <PreviewRow label="Razón Social" value={data.supplierSocialName} />
        {data.supplierFantasyName && (
          <PreviewRow label="Nombre Fantasía" value={data.supplierFantasyName} />
        )}

        <hr />
        <h6 className="text-muted mb-3">Totales</h6>

        <PreviewRow
          label="Total"
          value={formatAmount(data.amount, currency)}
        />
        <PreviewRow
          label="Base gravada 10%"
          value={formatAmount(data.amount10Percent, currency)}
        />
        <PreviewRow
          label="IVA 10%"
          value={formatAmount(data.iva10Percent, currency)}
        />
        <PreviewRow
          label="Base gravada 5%"
          value={formatAmount(data.amount5Percent, currency)}
        />
        <PreviewRow
          label="IVA 5%"
          value={formatAmount(data.iva5Percent, currency)}
        />
        <PreviewRow
          label="Exento"
          value={formatAmount(data.amountExempt, currency)}
        />
      </CCardBody>
      <CCardFooter>
        <CButton color="primary" onClick={onConfirm} disabled={submitting}>
          {submitting ? (
            <Spinner
              animation="grow"
              style={{
                height: "17px",
                width: "17px",
                marginRight: "8px",
              }}
            />
          ) : null}
          {submitting ? "Guardando..." : "Confirmar y guardar"}
        </CButton>
        &nbsp;
        <CButton color="secondary" onClick={onCancel} disabled={submitting}>
          Cancelar
        </CButton>
      </CCardFooter>
    </CCard>
  );
};

export default ReceivedDocumentCdcPreview;
